import React, { useState, useEffect } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid, LabelList,
  Label, Legend
} from "recharts";
import {
  FaMoneyBillWave, FaWallet, FaUniversity, FaArrowDown, FaArrowUp, FaBell
} from "react-icons/fa";
import CylinderInventoryProgress from "./CylinderInventoryProgress";
import { FilterPopup } from "./FilterPopup";
import { Tooltip as AntTooltip } from "antd";


/* -------------------- Refined Palettes -------------------- */
const PALETTE_TEAL = ["#005F73", "#0A9396", "#94D2BD", "#E9D8A6"];
const PALETTE_PURPLE = ["#4F46E5", "#7C3AED", "#C026D3", "#818CF8"];
const PALETTE_WARM = ["#F59E0B", "#EF4444", "#FB7185", "#FBBF24"];
const PALETTE_GREEN = ["#166534", "#10B981", "#84CC16", "#A3E635"];
const PALETTE_MIXED = ["#0EA5E9", "#6366F1", "#F43F5E", "#8B5CF6"];

const adjustColor = (hex, amount = -40) => {
  let col = hex.replace('#', '');
  if (col.length === 3) col = col.split('').map(c => c + c).join('');
  const num = parseInt(col, 16);
  let r = (num >> 16) + amount;
  let g = ((num >> 8) & 0x00ff) + amount;
  let b = (num & 0x0000ff) + amount;
  r = Math.max(Math.min(255, r), 0);
  g = Math.max(Math.min(255, g), 0);
  b = Math.max(Math.min(255, b), 0);
  return `rgb(${r}, ${g}, ${b})`;
};

const getContrastText = (hex) => {
  const c = hex.replace("#", "");
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 160 ? "#1e293b" : "#ffffff";
};

/* -------------------- Helper Functions -------------------- */
const getCurrentFY = () => {
  const today = new Date();
  const year = today.getFullYear();
  return today.getMonth() >= 3 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
};

const getCurrentWeekOfMonth = () => {
  const day = new Date().getDate();
  if (day <= 7) return "W1";
  if (day <= 14) return "W2";
  if (day <= 21) return "W3";
  return "W4";
};

const getCurrentQuarter = () => {
  const m = new Date().getMonth() + 1;
  if (m >= 4 && m <= 6) return "Q1";
  if (m >= 7 && m <= 9) return "Q2";
  if (m >= 10 && m <= 12) return "Q3";
  return "Q4";
};

const getRunningFYLabel = () => {
  const today = new Date();
  const year = today.getFullYear();
  const fyEnd = today.getMonth() >= 3 ? year + 1 : year;
  return `${fyEnd - 1}-${String(fyEnd).slice(2)}`;
};

const hasChartData = (data = []) =>
  Array.isArray(data) && data.some(d => Number(d?.value) > 0);

const buildFYList = (registeredFY) => {
  const today = new Date();
  const currentFYEnd = today.getMonth() >= 3 ? today.getFullYear() + 1 : today.getFullYear();
  const registrationFYEnd = 2000 + Number(registeredFY) + 1;
  const list = [];
  for (let y = currentFYEnd; y >= registrationFYEnd; y--) {
    list.push(`${y - 1}-${String(y).slice(2)}`);
  }
  return list;
};

const NoData = ({ text = "No data available" }) => (
  <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#94a3b8' }}>
    {text}
  </div>
);

const FILTER_KEYS = {
  PURCHASES: "filter_purchases",
  SALES: "filter_sales",
  CASH_CREDIT: "filter_cash_credit",
  PSE: "filter_pse",
  NC_DBC: "filter_nc_dbc",
};

/* -------------------- Responsive Hook -------------------- */
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    mediaQuery.addListener(handler);
    return () => mediaQuery.removeListener(handler);
  }, [query]);

  return matches;
};

/* -------------------- Main Component -------------------- */
export default function ProductDashboard({
  dashboard_data = {},
  purchaseSalesData,
  salesData,
  cashCreditData,
  purchaseSalesExpensesData,
  ncDbcData,
  onPurchaseSalesFilter = () => { },
  onSalesFilter = () => { },
  onCashCreditFilter = () => { },
  onPurchaseSalesExpensesFilter = () => { },
  onNcDbcFilter = () => { },
}) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1024px)');

  const [isPurchaseFiltered, setIsPurchaseFiltered] = useState(false);
  const [isCashCreditFiltered, setIsCashCreditFiltered] = useState(false);
  const [isNcDbcFiltered, setIsNcDbcFiltered] = useState(false);
  const [isSalesFiltered, setIsSalesFiltered] = useState(false);
  const [dashboardSnapshot, setDashboardSnapshot] = useState(null);

  useEffect(() => {
    if (!dashboardSnapshot && dashboard_data) {
      setDashboardSnapshot(dashboard_data);
    }
  }, [dashboard_data]);

  const formatNumber = (value) => {
    if (!value || value === "0" || value === 0) return "0";
    return parseFloat(value).toLocaleString("en-IN");
  };

  const getBankBalance = (bankCode) => {
    if (!dashboard_data?.bank || !Array.isArray(dashboard_data.bank)) return "0.00";
    const account = dashboard_data.bank.find((v) => v.BANK_CODE === bankCode);
    return account?.ACC_CB || "0.00";
  };

  const getLoadACBalance = () => {
    const tarBalance = parseFloat(getBankBalance("TAR ACCOUNT")) || 0;
    const starBalance = parseFloat(getBankBalance("STAR ACCOUNT")) || 0;
    return (tarBalance + starBalance).toFixed(2);
  };

  const normalizePurchaseData = (source = []) =>
    Array.isArray(source) ? source.filter(i => ["DOMESTIC", "COMMERCIAL", "ARB"].includes(i.type))
      .map(i => ({
        name: i.type === "DOMESTIC" ? "DOM" : i.type === "COMMERCIAL" ? "COM" : "ARB",
        value: Number(i.total || 0)
      })) : [];

  const normalizeCashCreditData = (source) => source?.CASH_SALES_REVENUE !== undefined ? [
    { name: "Cash", value: Number(source.CASH_SALES_REVENUE || 0) },
    { name: "Credit", value: Number(source.CREDIT_SALES_REVENUE || 0) }
  ] : [];

  const normalizeCategorySalesData = (source) => {
    if (!source) return [];
    return [
      { name: "DOM", value: Number(source.DOM_SALES || 0), qty: Number(source.DOM_SALE_QTY || 0) },
      { name: "COM", value: Number(source.COM_SALES || 0), qty: Number(source.COM_SALE_QTY || 0) },
      { name: "ARB", value: Number(source.ARB_SALES || 0), qty: Number(source.ARB_SALE_QTY || 0) },
    ];
  };

  const normalizeNcDbcData = (source) => {
    if (!source) return [];
    const nc = Number(source?.nc_dbc?.[0]?.nc || 0);
    const dbc = Number(source?.nc_dbc?.[0]?.dbc || 0);
    const rc = Number(source?.rc_tv?.[0]?.rc || 0);
    const tv = Number(source?.rc_tv?.[0]?.tv || 0);
    return [
      { name: "NC", value: nc },
      { name: "DBC", value: dbc },
      { name: "RC", value: rc },
      { name: "TV", value: tv },
    ];
  };

  const normalizePurchaseSalesExpenses = (source) => {
    if (!source) return [];
    const purchase = Number(source.PURCHASE_COST || 0);
    const sales = Number(source.SALES_REVENUE || 0);
    const expenses = Number(source.EXPENSES ?? source.TOTAL_EXPENSES ?? purchase * 0.1);
    return [
      { name: "Purchases", value: purchase },
      { name: "Sales", value: sales },
      { name: "Expenses", value: expenses },
    ];
  };

  const cylinderInventory = dashboard_data?.godown_stock
    ?.filter(item => item.CAT_NAME === "DOMESTIC" || item.CAT_NAME === "COMMERCIAL")
    ?.map(item => ({
      label: `Total-${item.PROD_CODE}`,
      weight: item.PRODUCT_NAME,
      fulls: parseInt(item.CLOSING_STOCK) || 0,
      empties: parseInt(item.CLOSING_STOCK_EMPTIES) || 0,
      fillColor: item.CAT_NAME === "DOMESTIC" ? "#005F73" : "#84CC16",
    })) || [];

  const normalizeArbInventory = (source = []) =>
    Array.isArray(source)
      ? source.filter(item => item.CAT_NAME === "ARB" || item.PRODUCT_CATEGORY === "ARB")
        .map(item => ({
          name: item.PRODUCT_NAME?.split("-")[1] || item.PROD_CODE,
          value: Number(item.FULLS || item.CLOSING_STOCK || 0),
        }))
      : [];

  const arbInventoryData = normalizeArbInventory(dashboard_data?.godown_stock);

  const buildDashboardPayload = ({ filterBy, financialYear, day, month, week, quarter }) => {
    const payload = { fy: financialYear };
    switch (filterBy) {
      case "Day": payload.date = day; break;
      case "Week": payload.week = week; payload.month = month; break;
      case "Month": payload.month = month; break;
      case "Qtr": payload.quarter = quarter; break;
      default: break;
    }
    return payload;
  };

  const handlePurchasesFilter = (filters) => {
    setIsPurchaseFiltered(true);
    onPurchaseSalesFilter(buildDashboardPayload(filters));
  };

  const handleSalesFilter = (filters) => {
    setIsSalesFiltered(true);
    onSalesFilter(buildDashboardPayload(filters));
  };

  const handleCashCreditFilter = (filters) => {
    setIsCashCreditFiltered(true);
    onCashCreditFilter(buildDashboardPayload(filters));
  };

  const handlePurchasesSalesExpensesFilter = (filters) => {
    onPurchaseSalesExpensesFilter(buildDashboardPayload(filters));
  };

  const handleNcDbcFilter = (filters) => {
    setIsNcDbcFiltered(true);
    onNcDbcFilter(buildDashboardPayload(filters));
  };

  const purchaseChartData = isPurchaseFiltered
    ? normalizePurchaseData(purchaseSalesData?.purchase_sales)
    : normalizePurchaseData(dashboardSnapshot?.purchase_sales);

  const salesChartData = isSalesFiltered
    ? normalizeCategorySalesData(salesData)
    : normalizeCategorySalesData(dashboardSnapshot);

  const cashCreditChartData = isCashCreditFiltered
    ? normalizeCashCreditData(cashCreditData)
    : [
      { name: "Cash", value: Number(dashboardSnapshot?.CASH_SALES_REVENUE || 0) },
      { name: "Credit", value: Number(dashboardSnapshot?.CREDIT_SALES_REVENUE || 0) },
    ];

  const pseChartData = purchaseSalesExpensesData?.PURCHASE_COST
    ? [
      { name: "Purchases", value: Number(purchaseSalesExpensesData.PURCHASE_COST) },
      { name: "Sales", value: Number(purchaseSalesExpensesData.SALES_REVENUE) },
      { name: "Expenses", value: Number(purchaseSalesExpensesData.PURCHASE_COST) * 0.1 },
    ]
    : [
      { name: "Purchases", value: Number(dashboardSnapshot?.PURCHASE_COST || 0) },
      { name: "Sales", value: Number(dashboardSnapshot?.SALES_REVENUE || 0) },
      { name: "Expenses", value: Number(dashboardSnapshot?.PURCHASE_COST || 0) * 0.1 },
    ];

  const ncDbcChartData = isNcDbcFiltered
    ? normalizeNcDbcData(ncDbcData)
    : normalizeNcDbcData({
      nc_dbc: dashboardSnapshot?.nc_dbc,
      rc_tv: dashboardSnapshot?.rc_tv,
    });

  const notifications = dashboard_data?.alerts?.length > 0 ? dashboard_data.alerts : [
    { id: 1, text: "Invoice pending approval", type: "warning", time: "2 min ago" },
    { id: 2, text: "Payment received", type: "success", time: "10 min ago" },
    { id: 3, text: "Low stock alert", type: "danger", time: "1 hr ago" },
  ];

  const styles = getResponsiveStyles(isMobile, isTablet);

  return (
    <div className="product-dashboard-root" style={styles.root}>
      <div style={styles.grid}>
        <CylinderInventoryCard cylinderInventory={cylinderInventory} isMobile={isMobile} isTablet={isTablet} />

        <div style={{ ...styles.card, padding: "6px", minHeight: "unset", height: "100%" }}>
          <div style={styles.cardHeader}>ARB Inventory</div>
          <div style={styles.chartBody}>
            <BarBlock palette={PALETTE_TEAL} data={arbInventoryData} />
          </div>
        </div>

        <div style={styles.tabletLeftColumn}>
          <div style={styles.kpiGrid}>
            <KPIBlock title="Cash Balance" value={`₹ ${formatNumber(dashboard_data?.cash_balance)}`} color="#0EA5E9" icon={<FaMoneyBillWave />} isMobile={isMobile} />
            <KPIBlock title="Payables" value={`₹ ${formatNumber(dashboard_data?.payables)}`} color="#EF4444" icon={<FaArrowDown />} isMobile={isMobile} />
            <KPIBlock title="Receivables" value={`₹ ${formatNumber(dashboard_data?.receivables)}`} color="#10B981" icon={<FaArrowUp />} isMobile={isMobile} />
            <KPIBlock title="Bank 1" value={`₹ ${formatNumber(dashboard_data?.bank?.[0]?.ACC_CB || "0")}`} color="#6366F1" icon={<FaUniversity />} isMobile={isMobile} />
            <KPIBlock title="Bank 2" value={`₹ ${formatNumber(dashboard_data?.bank?.[1]?.ACC_CB || "0")}`} color="#8B5CF6" icon={<FaUniversity />} isMobile={isMobile} />
            <KPIBlock title="Load AC" value={`₹ ${formatNumber(getLoadACBalance())}`} color="#F59E0B" icon={<FaWallet />} isMobile={isMobile} />
          </div>
          <div style={styles.notificationCard}>
            <div style={styles.notificationHeader}>
              <span>🔔 Notifications</span>
              <span style={styles.badge}>{notifications.length}</span>
            </div>
            <div className="notification-scroll" style={styles.notificationList}>
              {notifications.map((n, i) => (
                <div key={i} style={styles.notificationItem}>
                  <div style={{ ...styles.notificationDot, background: n.type === 'danger' ? '#ef4444' : '#f59e0b' }} />
                  <div>
                    <div style={styles.notificationText}>{n.text || n.message}</div>
                    <div style={styles.notificationTime}>{n.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* ----------------------Tablet View Nc/db--------------------------- */}
        {isTablet && (
          <div
            style={{
              ...styles.card,
              minHeight: "unset",
              gridColumn: isTablet ? "2 / 3" : "auto",
              gridRow: isTablet ? "2" : "auto",
            }}
          >
            <ChartCardBare
              title="NC / DBC Sales"
              showPeriod
              persistKey={FILTER_KEYS.NC_DBC}
              onFilterApply={handleNcDbcFilter}
              isMobile={isMobile}
              isTablet={isTablet}
            >
              <div style={styles.chartContainer}>
                {hasChartData(ncDbcChartData) ? (
                  <BarBlock palette={PALETTE_PURPLE} data={ncDbcChartData} />
                ) : (
                  <NoData />
                )}
              </div>
            </ChartCardBare>
          </div>
        )}
        {/* ---------------------- */}
        <div style={styles.bottomRow}>

          {!isTablet && (
            <div style={{ ...styles.card, minHeight: "unset" }}>
              <ChartCardBare
                title="NC / DBC Sales"
                showPeriod
                persistKey={FILTER_KEYS.NC_DBC}
                onFilterApply={handleNcDbcFilter}
                isMobile={isMobile}
                isTablet={isTablet}
              >
                <div style={styles.chartContainer}>
                  {hasChartData(ncDbcChartData) ? (
                    <BarBlock palette={PALETTE_PURPLE} data={ncDbcChartData} />
                  ) : (
                    <NoData />
                  )}
                </div>
              </ChartCardBare>
            </div>
          )}


          <div style={{ ...styles.card, padding: 0, overflow: "hidden" }}>
            <div style={styles.combinedChartGrid}>
              <div style={styles.innerChartCell}>
                <ChartCardBare title="Purchases" showPeriod persistKey={FILTER_KEYS.PURCHASES} onFilterApply={handlePurchasesFilter} isMobile={isMobile} isTablet={isTablet}>
                  <div style={styles.chartContainer}>
                    {hasChartData(purchaseChartData) ? <PieBlock palette={PALETTE_PURPLE} data={purchaseChartData} /> : <NoData text="No data available" />}
                  </div>
                </ChartCardBare>
              </div>

              <div style={{ ...styles.innerChartCell, ...(isMobile || isTablet ? {} : { borderLeft: "1px solid #f1f5f9" }) }}>
                <ChartCardBare title="Sales" showPeriod persistKey={FILTER_KEYS.SALES} onFilterApply={handleSalesFilter} isMobile={isMobile} isTablet={isTablet}>
                  <div style={styles.chartContainer}>
                    {hasChartData(salesChartData) ? <PieBlock palette={PALETTE_WARM} data={salesChartData} isDonut /> : <NoData text="No data available" />}
                  </div>
                </ChartCardBare>
              </div>

              <div style={{ ...styles.innerChartCell, ...(isMobile || isTablet ? {} : { borderTop: "1px solid #f1f5f9" }) }}>
                <ChartCardBare title="Cash / Credit Sales" showPeriod persistKey={FILTER_KEYS.CASH_CREDIT} onFilterApply={handleCashCreditFilter} isMobile={isMobile} isTablet={isTablet}>
                  <div style={styles.chartContainer}>
                    {hasChartData(cashCreditChartData) ? <PieBlock palette={PALETTE_MIXED} data={cashCreditChartData} isDonut /> : <NoData text="No data available" />}
                  </div>
                </ChartCardBare>
              </div>

              <div style={{ ...styles.innerChartCell, ...(isMobile || isTablet ? {} : { borderTop: "1px solid #f1f5f9", borderLeft: "1px solid #f1f5f9" }) }}>
                <ChartCardBare title="Purchases, Sales & Expenses" showPeriod persistKey={FILTER_KEYS.PSE} onFilterApply={handlePurchasesSalesExpensesFilter} isMobile={isMobile} isTablet={isTablet}>
                  <div style={styles.chartContainer}>
                    {hasChartData(pseChartData) ? <PieBlock palette={PALETTE_GREEN} data={pseChartData} /> : <NoData text="No data available" />}
                  </div>
                </ChartCardBare>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------- Sub-Components -------------------- */

const BarBlock = ({ palette, data }) => (
  <ResponsiveContainer width="100%" height="100%">
    <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fontWeight: 700, fill: '#475569' }} />
      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#64748b' }} />
      <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '11px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />
      <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={50}>
        {data.map((entry, index) => (
          <Cell key={`cell-${index}`} fill={palette[index % palette.length]} />
        ))}
        <LabelList dataKey="value" position="top" style={{ fontSize: '12px', fontWeight: '900', fill: '#1e293b' }} />
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

const PieBlock = ({ palette, data, isDonut }) => {
  const totalValue = data.reduce((sum, entry) => sum + entry.value, 0);
  const formatLabel = (val) => {
    const num = Number(val);
    if (num >= 10000000) return (num / 10000000).toFixed(1) + "Cr";
    if (num >= 100000) return (num / 100000).toFixed(1) + "L";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num.toFixed(0);
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius={isDonut ? "48%" : 0} outerRadius="70%" paddingAngle={2} stroke="#ffffff" strokeWidth={2} labelLine={false} isAnimationActive={false} minAngle={10}>
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={palette[index % palette.length]} />
          ))}
          <LabelList dataKey="value" position="inside" formatter={formatLabel} style={{ fontSize: "10px", fontWeight: "bold", fill: "#ffffff", textShadow: "0 1px 2px rgba(0,0,0,0.8)" }} angle={0} offset={10} />
        </Pie>
        {isDonut && (
          <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: "12px", fontWeight: "900", fill: "#0f172a" }}>
            {formatLabel(totalValue)}
          </text>
        )}
        <Tooltip formatter={(value, name) => [`₹${formatLabel(value)}`, name]} contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '11px', padding: '6px' }} />
        <Legend wrapperStyle={{ fontSize: "9px", paddingTop: "0px" }} iconSize={8} layout="horizontal" verticalAlign="bottom" align="center" />
      </PieChart>
    </ResponsiveContainer>
  );
};

const ChartCardBare = ({ title, children, showPeriod, onFilterApply, persistKey, isMobile, isTablet }) => {
  const saved = persistKey ? JSON.parse(localStorage.getItem(persistKey) || "{}") : {};
  const [filterBy, setFilterBy] = useState(saved.filterBy || "");
  const [financialYear, setFinancialYear] = useState(saved.financialYear || getRunningFYLabel());
  const [selectedDay, setSelectedDay] = useState(saved.day || new Date().toISOString().split("T")[0]);
  const [selectedWeek, setSelectedWeek] = useState(saved.week || "1");
  const [selectedMonth, setSelectedMonth] = useState(saved.month || "04");
  const [selectedQuarter, setSelectedQuarter] = useState(saved.quarter || "1");
  const [open, setOpen] = useState(false);

  const userData = JSON.parse(localStorage.getItem("userId") || "{}");
  const fyOptions = userData?.FY ? buildFYList(userData.FY) : [];

  const handleApply = () => {
    if (!filterBy) return;
    const payload = { filterBy, financialYear, day: selectedDay, week: selectedWeek, month: selectedMonth, quarter: selectedQuarter };
    if (persistKey) localStorage.setItem(persistKey, JSON.stringify(payload));
    onFilterApply(payload);
    setOpen(false);
  };

  const styles = getResponsiveStyles(isMobile, isTablet);

  return (
    <>
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <div style={styles.cardHeaderRow}>
  <AntTooltip title={title} placement="top">
    <span style={styles.responsiveTitle}>{title}</span>
  </AntTooltip>

  {showPeriod && (
    <button
      onClick={() => setOpen(true)}
      style={styles.filterBtn}
    >
      Period
    </button>
  )}
        </div>
        <div style={{ ...styles.chartContainer, minHeight: 0 }}>{children}</div>
      </div>

      <FilterPopup open={open} onClose={() => setOpen(false)}>
        <div style={styles.popupContainer}>
          <div style={styles.popupHeader}>
            <h4 style={styles.popupTitle}>Filter {title}</h4>
            <p style={styles.popupSubtitle}>Select the time range for this data</p>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Period Type</label>
            <select value={filterBy} onChange={e => setFilterBy(e.target.value)} style={styles.enhancedSelect}>
              <option value="">Choose Period...</option>
              <option value="Day">Daily</option>
              <option value="Week">Weekly</option>
              <option value="Month">Monthly</option>
              <option value="Qtr">Quarterly</option>
              <option value="Year">Yearly</option>
            </select>
          </div>

          {filterBy && (
            <div style={styles.dynamicFilters}>
              {filterBy === "Day" && (
                <div style={styles.filterGroup}>
                  <label style={styles.filterLabel}>Specific Date</label>
                  <input type="date" value={selectedDay} onChange={e => setSelectedDay(e.target.value)} style={styles.enhancedSelect} />
                </div>
              )}

              {["Week", "Month", "Qtr", "Year"].includes(filterBy) && (
                <div style={styles.filterGroup}>
                  <label style={styles.filterLabel}>Financial Year</label>
                  <select value={financialYear} onChange={e => setFinancialYear(e.target.value)} style={styles.enhancedSelect}>
                    {fyOptions.map(fy => <option key={fy}>{fy}</option>)}
                  </select>
                </div>
              )}

              {(filterBy === "Month" || filterBy === "Week") && (
                <div style={styles.filterGroup}>
                  <label style={styles.filterLabel}>Month</label>
                  <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} style={styles.enhancedSelect}>
                    <option value="04">APRIL</option><option value="05">MAY</option><option value="06">JUNE</option><option value="07">JULY</option>
                    <option value="08">AUGUST</option><option value="09">SEPTEMBER</option><option value="10">OCTOBER</option><option value="11">NOVEMBER</option>
                    <option value="12">DECEMBER</option><option value="01">JANUARY</option><option value="02">FEBRUARY</option><option value="03">MARCH</option>
                  </select>
                </div>
              )}

              {filterBy === "Week" && (
                <div style={styles.filterGroup}>
                  <label style={styles.filterLabel}>Week</label>
                  <select value={selectedWeek} onChange={(e) => setSelectedWeek(e.target.value)} style={styles.enhancedSelect}>
                    <option value="1">Week-1 (1–7)</option>
                    <option value="2">Week-2 (8–14)</option>
                    <option value="3">Week-3 (15–21)</option>
                    <option value="4">Week-4 (22–End)</option>
                  </select>
                </div>
              )}

              {filterBy === "Qtr" && (
                <div style={styles.filterGroup}>
                  <label style={styles.filterLabel}>Select Quarter</label>
                  <select value={selectedQuarter} onChange={e => setSelectedQuarter(e.target.value)} style={styles.enhancedSelect}>
                    <option value="1">Q1 (Apr - Jun)</option>
                    <option value="2">Q2 (Jul - Sep)</option>
                    <option value="3">Q3 (Oct - Dec)</option>
                    <option value="4">Q4 (Jan - Mar)</option>
                  </select>
                </div>
              )}
            </div>
          )}

          <div style={styles.popupFooter}>
            <button onClick={() => setOpen(false)} style={styles.cancelBtn}>CANCEL</button>
            <button onClick={handleApply} style={styles.enhancedApplyBtn}>APPLY FILTERS</button>
          </div>
        </div>
      </FilterPopup>
    </>
  );
};

const REQUIRED_WEIGHTS = ["5KG", "14.2KG", "19KG", "47KG"];

const normalizeWeight = (name = "") => {
  if (name.includes("5")) return "5KG";
  if (name.includes("14")) return "14.2KG";
  if (name.includes("19")) return "19KG";
  if (name.includes("47")) return "47KG";
  return name;
};

const CylinderInventoryCard = ({ cylinderInventory = [], isMobile, isTablet }) => {
  const cylinderStyles = getCylinderResponsiveStyles(isMobile, isTablet);

  return (
    <div style={cylinderStyles.card}>
      <div style={cylinderStyles.cardHeader}>Cylinder Inventory</div>
      <div style={cylinderStyles.container}>
        <div style={cylinderStyles.grid}>
          {REQUIRED_WEIGHTS.map((w, index) => {
            const found = cylinderInventory.find((c) => normalizeWeight(c.weight) === w);
            let extraGap = "0px";
            if (!isMobile && !isTablet) {
              if (index === 1) extraGap = "17px";
              if (index === 2) extraGap = "17px";
            }

            return (
              <div key={index} style={{ ...cylinderStyles.item, marginRight: extraGap }}>
                <CylinderInventoryProgress weight={found?.weight || w} fulls={found?.fulls || 0} empties={found?.empties || 0} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const KPIBlock = ({ title, value, color, icon, isMobile }) => {
  const styles = getResponsiveStyles(isMobile, false);

  return (
    <div style={{ ...styles.kpiCard, borderLeft: `4px solid ${color}` }}>
      <div style={styles.kpiHeader}>
        <span style={styles.kpiIcon}>{icon}</span>
        <span style={styles.kpiTitle}>{title}</span>
      </div>
      <div style={{ ...styles.kpiValue, color }}>{value}</div>
    </div>
  );
};

/* -------------------- Responsive Styles -------------------- */
const getResponsiveStyles = (isMobile, isTablet) => ({
  root: {
    height: isMobile || isTablet ? "auto" : "90vh",
    width: "100%",
    background: "#f1f5f9",
    overflowY: isMobile || isTablet ? "auto" : "hidden",
    overflowX: "hidden",
    minHeight: isMobile || isTablet ? "100vh" : "90vh",

    /* 👇 KEY DESKTOP FIX */
    display: "flex",
    justifyContent: "center",
  },



  grid: {
    display: "grid",
    height: isMobile ? "auto" : "100%",
    width: "100%",
    padding: isMobile ? "8px" : isTablet ? "12px" : "10px",
    boxSizing: "border-box",
    gap: isMobile ? "8px" : isTablet ? "12px" : "10px",
    gridTemplateColumns: isMobile
      ? "1fr"
      : isTablet
        ? "1fr 1fr"
        : "0.95fr 1.7fr 0.8fr",

    gridTemplateRows: isMobile ? "auto" : isTablet ? "auto" : "0.52fr 0.78fr",
  },
  tabletLeftColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    gridColumn: isTablet ? "1 / 2" : "auto",
    gridRow: isTablet ? "2" : "auto",
  },


  card: {
    background: "#ffffff",
    borderRadius: isMobile ? "8px" : "10px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
    padding: isMobile ? "8px" : "10px",
    display: "flex",
    flexDirection: "column",
    height: isMobile || isTablet ? "auto" : "100%",
    minHeight: isMobile ? "200px" : isTablet ? "250px" : 0,
    overflow: "hidden",
  },

  cardHeader: {
    fontSize: isMobile ? "10px" : "11px",
    fontWeight: 800,
    textTransform: "uppercase",
    color: "#1e293b",
    marginBottom: isMobile ? "4px" : "6px",
    letterSpacing: "0.4px",
    flexShrink: 0,
  },

  cardHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: isMobile ? "3px 6px" : "4px 8px",
    borderBottom: "1px solid #f1f5f9",
    flexShrink: 0,
  },

  responsiveTitle: {
    fontSize: isMobile ? "9px" : "10px",
    fontWeight: 800,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  chartBody: {
    flex: 1,
    minHeight: isMobile ? "180px" : isTablet ? "200px" : 0,
    width: "100%",
  },

  chartContainer: {
    flex: "1 1 auto",
    width: "100%",
    height: "100%",
    minHeight: isMobile ? "180px" : isTablet ? "200px" : 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  popupContainer: {
    padding: isMobile ? "12px" : "16px",
    minWidth: isMobile ? "auto" : "280px",
    display: "flex",
    flexDirection: "column",
    gap: isMobile ? "10px" : "12px",
  },

  popupHeader: { marginBottom: "4px" },

  popupTitle: {
    margin: 0,
    fontSize: isMobile ? "13px" : "14px",
    fontWeight: "800",
    color: "#1e293b",
  },

  popupSubtitle: {
    margin: "2px 0 0",
    fontSize: isMobile ? "10px" : "11px",
    color: "#94a3b8",
  },

  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    width: "100%",
  },

  filterLabel: {
    fontSize: isMobile ? "9px" : "10px",
    fontWeight: "700",
    color: "#64748b",
    textTransform: "uppercase",
  },

  enhancedSelect: {
    width: "100%",
    fontSize: isMobile ? "11px" : "12px",
    padding: isMobile ? "7px" : "8px",
    borderRadius: "6px",
    border: "1px solid #cbd5e1",
    background: "#fff",
    color: "#334155",
    outline: "none",
    cursor: "pointer",
    boxSizing: "border-box",
  },

  dynamicFilters: {
    display: "flex",
    flexDirection: "column",
    gap: isMobile ? "10px" : "12px",
    padding: isMobile ? "10px" : "12px",
    background: "#f8fafc",
    borderRadius: "8px",
    border: "1px solid #f1f5f9",
  },

  popupFooter: {
    display: "flex",
    gap: isMobile ? "6px" : "8px",
    marginTop: isMobile ? "8px" : "10px",
  },

  cancelBtn: {
    flex: 1,
    padding: isMobile ? "8px" : "10px",
    fontSize: isMobile ? "10px" : "11px",
    fontWeight: "700",
    borderRadius: "6px",
    border: "1px solid #e2e8f0",
    background: "#fff",
    color: "#64748b",
    cursor: "pointer",
  },

  enhancedApplyBtn: {
    flex: 2,
    padding: isMobile ? "8px" : "10px",
    fontSize: isMobile ? "10px" : "11px",
    fontWeight: "700",
    borderRadius: "6px",
    border: "none",
    background: "#4f46e5",
    color: "#fff",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(79, 70, 229, 0.2)",
  },

  rightSidebar: {
    display: "flex",
    flexDirection: "column",
    height: isMobile || isTablet ? "auto" : "100%",
    gap: "5px",
    gridColumn: isTablet ? "1 / -1" : "auto", // 👈 KEY LINE
  },


  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "6px",
  },

  kpiCard: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: isMobile ? "6px 6px" : "6px 8px",
    minHeight: isMobile ? "40px" : "44px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },

  kpiHeader: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    marginBottom: "2px",
  },

  kpiTitle: {
    fontSize: isMobile ? "8px" : "9px",
    fontWeight: 700,
    textTransform: "uppercase",
    color: "#64748b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  kpiValue: {
    fontSize: isMobile ? "9px" : "10px",
    fontWeight: 900,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  kpiIcon: {
    fontSize: isMobile ? "11px" : "13px",
  },

  notificationCard: {
    background: "#fff",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
    display: "flex",
    flexDirection: "column",
    padding: isMobile ? "8px" : "10px",
    flex: 1,
    minHeight: isMobile ? "150px" : "auto",
  },

  notificationHeader: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: isMobile ? "10px" : "11px",
    fontWeight: 800,
    borderBottom: "1px solid #f1f5f9",
    paddingBottom: "6px",
    flexShrink: 0,
  },

  badge: {
    background: "#ef4444",
    color: "#fff",
    borderRadius: "10px",
    fontSize: isMobile ? "8px" : "9px",
    padding: "2px 8px",
    fontWeight: 700,
  },

  notificationList: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    marginTop: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },

  notificationItem: {
    display: "flex",
    gap: "8px",
    padding: isMobile ? "6px" : "8px",
    borderRadius: "8px",
    background: "#f8fafc",
    border: "1px solid #f1f5f9",
  },

  notificationDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    marginTop: "5px",
    flexShrink: 0,
  },

  notificationText: {
    fontSize: isMobile ? "9px" : "10px",
    fontWeight: 600,
    color: "#334155",
    lineHeight: "1.4",
  },

  notificationTime: {
    fontSize: isMobile ? "8px" : "9px",
    color: "#94a3b8",
    marginTop: "2px",
  },

  bottomRow: {
    /* ⬇️ KEY FIX */
    gridColumn: isTablet
      ? "1 / -1"   // take full width on tablet
      : isMobile
        ? "1"
        : "1 / span 2",

    display: "grid",

    gridTemplateColumns: isTablet
      ? "1fr"      // 2×2 grid handled inside
      : isMobile
        ? "1fr"
        : "0.65fr 1.35fr",

    gap: isMobile ? "8px" : isTablet ? "12px" : "10px",
    height: isMobile || isTablet ? "auto" : "100%",
    minHeight: 0,
    overflow: "hidden",
  },


  combinedChartGrid: {
    display: isTablet ? "grid" : isMobile ? "grid" : "flex",

    gridTemplateColumns: isTablet
      ? "1fr 1fr"
      : isMobile
        ? "1fr"
        : undefined,

    gridAutoRows: isTablet ? "1fr" : "auto",

    gap: isTablet ? "12px" : isMobile ? "8px" : 0,
    width: "100%",
    height: "100%",
  },


  innerChartCell: {
    flex: isMobile ? "unset" : isTablet ? "unset" : "1 1 0",
    maxWidth: isMobile || isTablet ? "100%" : "25%",
    minWidth: 0,

    padding: isTablet ? "0" : isMobile ? "0" : "4px",

    display: "flex",
    flexDirection: "column",
    overflow: "hidden",

    minHeight: isTablet ? "260px" : isMobile ? "200px" : "auto",
  },


  filterBtn: {
    fontSize: "7px",
    padding: isMobile ? "2px 8px" : "3px 10px",
    border: "1px solid #e2e8f0",
    borderRadius: "20px",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 600,
    color: "#475569",
    whiteSpace: "nowrap",
  },

  longTitleContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },

  longTitleText: {
    fontSize: isMobile ? "8px" : "9px",
    fontWeight: 800,
    textTransform: "uppercase",
    color: "#475569",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "75%",
  },
});

const getCylinderResponsiveStyles = (isMobile, isTablet) => ({
  card: {
    background: "#fff",
    borderRadius: "10px",
    padding: isMobile ? "8px" : "6px",
    border: "1px solid #e2e8f0",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
    height: isMobile || isTablet ? "auto" : "100%",
    minHeight: isMobile ? "200px" : isTablet ? "250px" : "auto",
  },

  cardHeader: {
    fontSize: isMobile ? "10px" : "11px",
    fontWeight: 800,
    color: "#1e293b",
    textTransform: "uppercase",
    marginBottom: "4px",
    letterSpacing: "0.5px",
  },

  container: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingRight: isMobile ? "0" : "8px",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
    gap: isMobile ? "8px" : "6px",
    width: "100%",
    justifyItems: "center",
    alignItems: "center",
  },

  item: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-end",
  },
});